import { useFrame, useThree } from '@react-three/fiber'
import { RefObject, useEffect, useRef } from 'react'
import { BufferAttribute, BufferGeometry, Group, NormalBlending, Points, PointsMaterial } from 'three'
import {
    type FootstepStepEvent,
    type FootstepTimelineRef,
    computeFootPlacement,
} from './footstepConfig'
import { useFootstepEffectTextures } from './useFootstepEffectTextures'
import { store } from '@/src/stores/store'

const dustSettings = {
    color: '#fef1e1',
    count: 10,
    previewCount: 10,
    size: 0.2,
    previewSize: 0.4,
    previewEndSize: 1.2,
    previewForwardOffset: -0.3,
    previewHeightOffset: -0.2,
    endSize: 0.7,
    opacity: 1.85,
    speed: 4,
    spread: 0.25,
    forwardScatter: 0.5,
    upwardVelocity: 0.4,
    lifetime: 1.0,
    drag: 5.0,
    spawnDelay: 0.00,
    rotationSpeed: 8.0,
}

type DustBurst = {
    age: number
    geometry: BufferGeometry
    material: PointsMaterial
    points: Points
    positions: Float32Array
    rotationUniforms: { uTime: { value: number } }
    velocities: Float32Array
}

type EffectRunDustProps = {
    anchorRef: RefObject<Group | null>
    timelineRef: FootstepTimelineRef
}

function createDustMaterial(texture: any, size: number): PointsMaterial {
    const material = new PointsMaterial({
        color: dustSettings.color,
        size,
        alphaMap: texture,
        transparent: true,
        opacity: dustSettings.opacity,
        depthWrite: false,
        blending: NormalBlending,
        sizeAttenuation: true,
        toneMapped: false,
    })
    material.userData.rotationUniforms = { uTime: { value: 0 } }
    material.onBeforeCompile = (shader) => {
        shader.uniforms.uTime = material.userData.rotationUniforms.uTime
        shader.vertexShader = shader.vertexShader
            .replace('#include <common>', `#include <common>
attribute float aRotation;
attribute float aSpinSpeed;
uniform float uTime;
varying float vParticleRotation;`)
            .replace('#include <begin_vertex>', `#include <begin_vertex>
vParticleRotation = aRotation + (uTime * aSpinSpeed);`)
        shader.fragmentShader = shader.fragmentShader
            .replace('#include <common>', `#include <common>
varying float vParticleRotation;
vec2 getRotatedPointCoord(){vec2 c=gl_PointCoord-.5;float s=sin(vParticleRotation),cs=cos(vParticleRotation);return vec2(c.x*cs-c.y*s,c.x*s+c.y*cs)+.5;}`)
            .replace('#include <map_particle_fragment>', `#if defined( USE_MAP ) || defined( USE_ALPHAMAP )
vec2 rotatedPointCoord = getRotatedPointCoord();
vec2 uv = ( uvTransform * vec3( rotatedPointCoord.x, 1.0 - rotatedPointCoord.y, 1 ) ).xy;
#endif
#ifdef USE_MAP
diffuseColor *= texture2D( map, uv );
#endif
#ifdef USE_ALPHAMAP
diffuseColor.a *= texture2D( alphaMap, uv ).g;
#endif`)
    }
    material.customProgramCacheKey = () => 'footstep-dust-v4'
    return material
}

export function EffectRunDust({ anchorRef, timelineRef }: EffectRunDustProps) {
    const { scene } = useThree()
    const activeBurstsRef = useRef<DustBurst[]>([])
    const isPreviewMode =
        store.appFlowPhase === 'configurator' &&
        store.configuratorActiveTab === 'effect' &&
        store.footstepEffect === 'dust'
    const pendingBurstsRef = useRef<Array<{ step: FootstepStepEvent; delayRemaining: number }>>([])
    const { dustTexture } = useFootstepEffectTextures()

    useEffect(() => {
        return () => {
            for (const b of activeBurstsRef.current) {
                scene.remove(b.points); b.geometry.dispose(); b.material.dispose()
            }
        }
    }, [])

    function spawnBurst(step: FootstepStepEvent) {
        const placement = computeFootPlacement(anchorRef, step.side)
        if (!placement) return

        const count = Math.max(1, Math.floor(isPreviewMode ? dustSettings.previewCount : dustSettings.count))
        const positions = new Float32Array(count * 3)
        const velocities = new Float32Array(count * 3)
        const rotations = new Float32Array(count)
        const spinSpeeds = new Float32Array(count)
        const geometry = new BufferGeometry()
        geometry.setAttribute('position', new BufferAttribute(positions, 3))
        geometry.setAttribute('aRotation', new BufferAttribute(rotations, 1))
        geometry.setAttribute('aSpinSpeed', new BufferAttribute(spinSpeeds, 1))

        for (let i = 0; i < count; i++) {
            rotations[i] = Math.random() * Math.PI * 2
            spinSpeeds[i] = (Math.random() * 2 - 1) * dustSettings.rotationSpeed
        }

        for (let i = 0; i < count; i++) {
            const i3 = i * 3
            const lat = (Math.random() * 2 - 1) * dustSettings.spread
            const fwd = (Math.random() * 2 - 1) * dustSettings.forwardScatter
            const up = (0.35 + Math.random() * 0.65) * dustSettings.upwardVelocity
            velocities[i3] = placement.right.x * lat + placement.forward.x * fwd + placement.up.x * up
            velocities[i3 + 1] = placement.right.y * lat + placement.forward.y * fwd + placement.up.y * up
            velocities[i3 + 2] = placement.right.z * lat + placement.forward.z * fwd + placement.up.z * up
        }

        const startSize = isPreviewMode ? dustSettings.previewSize : dustSettings.size
        const endSize = isPreviewMode ? dustSettings.previewEndSize : dustSettings.endSize
        const material = createDustMaterial(dustTexture, startSize)
        const rotationUniforms = material.userData.rotationUniforms as { uTime: { value: number } }
        const points = new Points(geometry, material)
        const burstPosition = placement.position.clone()
        if (isPreviewMode) {
            burstPosition.addScaledVector(placement.forward, dustSettings.previewForwardOffset)
            burstPosition.addScaledVector(placement.up, dustSettings.previewHeightOffset)
        }
        points.position.copy(burstPosition)
        scene.add(points)
        activeBurstsRef.current.push({ age: 0, geometry, material, points, positions, rotationUniforms, velocities })
    }

    useFrame((_, delta) => {
        const safeDelta = Math.min(delta, 1 / 60)
        const timeline = timelineRef.current

        for (let i = pendingBurstsRef.current.length - 1; i >= 0; i--) {
            const p = pendingBurstsRef.current[i]
            p.delayRemaining -= safeDelta
            if (p.delayRemaining > 0) continue
            spawnBurst(p.step)
            pendingBurstsRef.current.splice(i, 1)
        }

        for (let i = activeBurstsRef.current.length - 1; i >= 0; i--) {
            const b = activeBurstsRef.current[i]
            b.age += safeDelta
            const t = b.age / dustSettings.lifetime
            if (t >= 1) {
                scene.remove(b.points)
                b.geometry.dispose()
                b.material.dispose()
                activeBurstsRef.current.splice(i, 1)
                continue
            }
            const drag = Math.exp(-dustSettings.drag * safeDelta)
            for (let p = 0; p < b.positions.length; p += 3) {
                b.positions[p] += b.velocities[p] * dustSettings.speed * safeDelta
                b.positions[p + 1] += b.velocities[p + 1] * dustSettings.speed * safeDelta
                b.positions[p + 2] += b.velocities[p + 2] * dustSettings.speed * safeDelta
                b.velocities[p] *= drag
                b.velocities[p + 1] *= drag
                b.velocities[p + 2] *= drag
            }
            b.geometry.attributes.position.needsUpdate = true
            b.rotationUniforms.uTime.value = b.age
            b.material.opacity = dustSettings.opacity * (1 - t)
            const startSize = isPreviewMode ? dustSettings.previewSize : dustSettings.size
            const endSize = isPreviewMode ? dustSettings.previewEndSize : dustSettings.endSize
            b.material.size = startSize + (endSize - startSize) * t
        }

        if (!timeline.footstepsEnabled) {
            pendingBurstsRef.current = []
            return
        }

        for (const step of timeline.steps) {
            pendingBurstsRef.current.push({
                step,
                delayRemaining: dustSettings.spawnDelay,
            })
        }
    })

    return null
}








